import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import axios from "./axios_config";
import { BASE_API_URL } from "./common/endpoints";
import { setChatLanguage } from "./cometchat/chat_client";
import { isSessionRejected, profileRetryDelay } from "./common/utils/auth_session";
import { describeError, errorMessage } from "./common/utils/error_message";

const UserDataContext = createContext();

const defaultAccessRights = {
	isSuperadmin: false,
	isAdminOfCourses: false,
	isAdminOfGroups: false,
	isCourseFeedbackRecipient: false,
	isAuthor: false,
	isAdminOfPlatform: false,
	isAdminOfPlatformUsers: false,
	isAdminOfPlatformCourses: false,
	isAdminOfPlatformGroups: false,
	isAdminOfPlatformQuestions: false,
	isAdminOfPlatformAnalytics: false,
	isAdminOfPlatformHomePage: false,
	isMentor: false,
	isLeader: false,
	isOperatorOfSupport: false,
};

// Single source of truth for "user has access to the /admin section".
// Both the header (showing the Admin button) and admin_page (gating entry)
// must use this — if they diverge a user can see the button but be redirected away.
export const hasAnyAdminAccess = rights => {
	if (!rights) return false;
	return Boolean(
		rights.isSuperadmin ||
		rights.isAdminOfCourses ||
		rights.isAdminOfGroups ||
		rights.isAuthor ||
		rights.isCourseFeedbackRecipient ||
		rights.isOperatorOfSupport ||
		rights.isAdminOfPlatformUsers ||
		rights.isAdminOfPlatformCourses ||
		rights.isAdminOfPlatformGroups ||
		rights.isAdminOfPlatformQuestions ||
		rights.isAdminOfPlatformAnalytics ||
		rights.isAdminOfPlatformHomePage,
	);
};

const arraysEqual = (a, b) => {
	if (a === b) return true;
	if (!Array.isArray(a) || !Array.isArray(b)) return false;
	if (a.length !== b.length) return false;
	return a.every((val, i) => val === b[i]);
};

const getInitialContentLanguages = () => {
	const stored = localStorage.getItem("contentLanguages");
	if (stored) {
		try {
			const parsed = JSON.parse(stored);
			if (Array.isArray(parsed) && parsed.length > 0) {
				return parsed;
			}
		} catch (e) {
			// ignore
		}
	}
	return ["ru"];
};

const getInitialInterfaceLanguage = () => {
	const stored = localStorage.getItem("i18nextLng");
	if (stored) return stored;

	const language = navigator.language || navigator.userLanguage;
	if (language.startsWith("uk")) return "uk";
	if (language.startsWith("ru")) return "ru";
	return "en";
};

export const useUserDataState = () => {
	const { i18n } = useTranslation();

	const [userData, setUserData] = useState({});
	const [isAuthChecked, setIsAuthChecked] = useState(false);
	const [authError, setAuthError] = useState(null);
	// "checking" | "authenticated" | "anonymous" | "unreachable". Unreachable means the server could
	// not be asked — never a reason to log anyone out; the profile is retried until it answers.
	const [authStatus, setAuthStatus] = useState("checking");
	const profileRequestRef = useRef(0);
	const authStatusRef = useRef("checking");
	authStatusRef.current = authStatus;
	const retryTimerRef = useRef(null);
	const retryAttemptRef = useRef(0);
	const [accessRights, setAccessRights] = useState(defaultAccessRights);
	const [userQuestionsList, setUserQuestionsList] = useState([]);
	//           language settings
	const [interfaceLanguage, setInterfaceLanguage] = useState(getInitialInterfaceLanguage);
	const [contentLanguages, setContentLanguages] = useState(getInitialContentLanguages);

	const isInterfaceLangFirstInit = useRef(true);
	const hasUserChangedInterfaceLang = useRef(false);
	const hasPatchedMissingLanguages = useRef(false);

	const defineAccessRights = useCallback(userData => {
		const isAdminOfPlatform = userData.user_access?.is_admin_of_platform;

		setAccessRights({
			...defaultAccessRights,
			isSuperadmin: userData.user_role === "superadmin",
			isAdminOfCourses: userData.user_access?.is_admin_of_courses,
			isAdminOfGroups: userData.user_access?.is_admin_of_groups,
			isCourseFeedbackRecipient: userData.user_access?.is_course_feedback_recipient,
			isAuthor: userData.user_access?.is_author,
			isAdminOfPlatform,
			isAdminOfPlatformUsers: isAdminOfPlatform && userData.user_access?.admin_of_platform?.users,
			isAdminOfPlatformCourses:
				isAdminOfPlatform && userData.user_access?.admin_of_platform?.courses,
			isAdminOfPlatformGroups: isAdminOfPlatform && userData.user_access?.admin_of_platform?.groups,
			isAdminOfPlatformQuestions:
				isAdminOfPlatform && userData.user_access?.admin_of_platform?.questions,
			isAdminOfPlatformAnalytics:
				isAdminOfPlatform && userData.user_access?.admin_of_platform?.analytics,
			isAdminOfPlatformHomePage:
				isAdminOfPlatform && userData.user_access?.admin_of_platform?.homepage,
			isMentor: userData.user_access?.is_mentor,
			isLeader: userData.user_access?.is_leader,
			isOperatorOfSupport: userData.user_access?.is_operator_of_support,
		});
	}, []);

	const unreadAnswersCount = useMemo(
		() =>
			userQuestionsList.filter(question => question.question_answer?.answer_read === false).length,
		[userQuestionsList],
	);

	const fetchUserDataRef = useRef(null);

	const fetchUserData = useCallback(async () => {
		// Only the latest call may write: login fires this and navigates at once, and an older
		// response landing last would put back the state it replaced.
		const requestId = ++profileRequestRef.current;
		clearTimeout(retryTimerRef.current);

		const becomeAnonymous = message => {
			setUserData({});
			setAccessRights(defaultAccessRights);
			setAuthError(message);
			setAuthStatus("anonymous");
			setIsAuthChecked(true);
		};

		if (!localStorage.getItem("access_token")) {
			becomeAnonymous("Token is missing.");
			return;
		}

		// A token without a known user — right after login — is undecided again, so a guard on
		// the page login navigates to waits for the profile instead of bouncing to home.
		if (authStatusRef.current === "anonymous") {
			setAuthStatus("checking");
			setIsAuthChecked(false);
			setAuthError(null);
		}

		try {
			const response = await axios.get(`/v1/auth/profile`);
			if (requestId !== profileRequestRef.current) return;
			const user = response.data.user;

			retryAttemptRef.current = 0;
			setUserData(user);
			defineAccessRights(user);
			setAuthError(null);
			setAuthStatus("authenticated");
			setIsAuthChecked(true);
		} catch (error) {
			if (requestId !== profileRequestRef.current) return;

			if (isSessionRejected(error)) {
				becomeAnonymous(error.response?.data?.message || errorMessage(error));
				return;
			}

			// The server could not answer. Keep whoever is signed in and stay undecided
			// (isAuthChecked untouched), so no guard reads the outage as a logout.
			console.error("Profile unreachable, retrying:", describeError(error));
			setAuthStatus("unreachable");
			const delay = profileRetryDelay(retryAttemptRef.current++);
			retryTimerRef.current = setTimeout(() => fetchUserDataRef.current?.(), delay);
		}
	}, [defineAccessRights]);
	fetchUserDataRef.current = fetchUserData;

	// While unreachable, retry at once when the connection or the tab comes back.
	useEffect(() => {
		if (authStatus !== "unreachable") return;
		const retryNow = () => {
			if (document.visibilityState === "visible") fetchUserDataRef.current?.();
		};
		window.addEventListener("online", retryNow);
		document.addEventListener("visibilitychange", retryNow);
		return () => {
			window.removeEventListener("online", retryNow);
			document.removeEventListener("visibilitychange", retryNow);
		};
	}, [authStatus]);

	useEffect(() => () => clearTimeout(retryTimerRef.current), []);

	useEffect(() => {
		const patchMissingContentLanguages = async () => {
			if (
				userData?._id &&
				(!userData.user_content_languages || userData.user_content_languages.length === 0) &&
				!hasPatchedMissingLanguages.current
			) {
				hasPatchedMissingLanguages.current = true;

				const storedLangs = localStorage.getItem("contentLanguages");
				let defaultLang = ["ru"];

				if (storedLangs) {
					try {
						const parsed = JSON.parse(storedLangs);
						if (Array.isArray(parsed) && parsed.length > 0) {
							defaultLang = parsed;
						}
					} catch (e) {
						// ignore
					}
				}

				try {
					await axios.patch(`${BASE_API_URL}/v1/users/me`, {
						user_content_languages: defaultLang,
					});

					setUserData(prev => ({
						...prev,
						user_content_languages: defaultLang,
					}));
					setContentLanguages(defaultLang);
				} catch (error) {
					console.error("Error patching missing content languages:", describeError(error));
				}
			}
		};

		patchMissingContentLanguages();
	}, [userData]);

	const updateContentLanguages = useCallback(
		langs => {
			const safeLangs = Array.isArray(langs) && langs.length > 0 ? langs : ["ru"];

			setContentLanguages(safeLangs);
			localStorage.setItem("contentLanguages", JSON.stringify(safeLangs));

			const userId = userData?._id;
			if (userId) {
				axios
					.patch(`${BASE_API_URL}/v1/users/me`, {
						user_content_languages: safeLangs,
					})
					.catch(e => {
						console.error("Error updating content languages", describeError(e));
					});
			}
		},
		[userData?._id],
	);

	useEffect(() => {
		const userLangs = userData?.user_content_languages;

		if (!Array.isArray(userLangs) || userLangs.length === 0) return;

		setContentLanguages(prev => {
			if (arraysEqual(prev, userLangs)) {
				return prev;
			}

			localStorage.setItem("contentLanguages", JSON.stringify(userLangs));
			return userLangs;
		});
	}, [userData]);

	const updateInterfaceLanguage = useCallback(
		async lang => {
			localStorage.setItem("i18nextLng", lang);

			hasUserChangedInterfaceLang.current = true;

			setInterfaceLanguage(lang);

			if (isInterfaceLangFirstInit.current) return;

			const userId = userData?._id;
			if (userId) {
				try {
					await axios.patch(`${BASE_API_URL}/v1/users/me`, {
						user_lang: lang,
					});
				} catch (error) {
					console.error("Error updating interface languages:", describeError(error));
				}
			}
		},
		[userData?._id],
	);

	useEffect(() => {
		const applyLanguage = () => {
			i18n.changeLanguage(interfaceLanguage);
			setChatLanguage(interfaceLanguage);
			isInterfaceLangFirstInit.current = false;
		};

		if (!i18n.isInitialized) {
			const onInit = () => {
				applyLanguage();
			};

			i18n.on("initialized", onInit);

			return () => {
				i18n.off("initialized", onInit);
			};
		} else {
			applyLanguage();
		}
	}, [i18n, interfaceLanguage]);

	useEffect(() => {
		const userLang = userData?.user_lang;
		if (!userLang) return;

		if (hasUserChangedInterfaceLang.current) return;

		localStorage.setItem("i18nextLng", userLang);
		setInterfaceLanguage(userLang);
	}, [userData]);

	const fetchUserQuestions = useCallback(async () => {
		if (!userData._id) return;

		try {
			const response = await axios.get(
				`${BASE_API_URL}/v1/questions/fromauthor/${userData._id}?limit=10000`,
			);
			const questions = response.data.items;

			setUserQuestionsList(questions);
		} catch (error) {
			console.error("Error fetching user questions:", describeError(error));
		}
	}, [userData?._id]);

	useEffect(() => {
		if (!userData._id) return;

		fetchUserQuestions();

		const interval = setInterval(
			() => {
				fetchUserQuestions();
			},
			5 * 60 * 1000,
		);

		return () => clearInterval(interval);
	}, [userData?._id, fetchUserQuestions]);

	useEffect(() => {
		if (!userData._id) return;

		const ping = () => {
			axios.get("/v1/ping").catch(e => console.error("Presence ping failed:", describeError(e)));
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") ping();
		};

		ping();
		const interval = setInterval(ping, 60_000);
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			clearInterval(interval);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [userData._id]);

	return {
		userData,
		setUserData,
		accessRights,
		fetchUserData,
		isAuthChecked,
		authError,
		authStatus,
		interfaceLanguage,
		unreadAnswersCount,
		userQuestionsList,
		fetchUserQuestions,
		setUserQuestionsList,
		contentLanguages,
		setContentLanguages,
		updateContentLanguages,
		updateInterfaceLanguage,
	};
};

export const UserDataProvider = ({ children }) => {
	const {
		userData,
		fetchUserData,
		setUserData,
		isAuthChecked,
		authError,
		authStatus,
		accessRights,
		interfaceLanguage,
		unreadAnswersCount,
		userQuestionsList,
		fetchUserQuestions,
		setUserQuestionsList,
		contentLanguages,
		setContentLanguages,
		updateContentLanguages,
		updateInterfaceLanguage,
	} = useUserDataState();

	return (
		<UserDataContext.Provider
			value={{
				userData,
				fetchUserData,
				setUserData,
				isAuthChecked,
				authError,
				authStatus,
				accessRights,
				interfaceLanguage,
				unreadAnswersCount,
				userQuestionsList,
				fetchUserQuestions,
				setUserQuestionsList,
				contentLanguages,
				setContentLanguages,
				updateContentLanguages,
				updateInterfaceLanguage,
			}}
		>
			{children}
		</UserDataContext.Provider>
	);
};

export const useUserDataContext = () => {
	return useContext(UserDataContext);
};
